import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { twilioA2pApi } from "../lib/api";
import { CalendarClock, Loader2, Save, AlertTriangle, Plus, X } from "lucide-react";

const UNIT_OPTIONS = [
  { value: "hours", label: "Hours" },
  { value: "days", label: "Days" },
];

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-slate-300"}`}
    >
      <span
        className="inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform"
        style={{ transform: checked ? "translateX(18px)" : "translateX(2px)" }}
      />
    </button>
  );
}

function CampaignWarning({ status, testId }) {
  if (status === "VERIFIED") return null;
  return (
    <div className="mt-1.5 flex items-center gap-1.5 text-[11.5px] text-amber-700" data-testid={testId}>
      <AlertTriangle className="h-3 w-3 shrink-0" /> Campaign pending approval — messages will not send until verified.
    </div>
  );
}

const timingInputClass = "w-16 h-8 px-2 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";
const timingSelectClass = "h-8 px-2 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";

export default function AppointmentMessagingCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [campaignStatus, setCampaignStatus] = useState(null);

  const load = async () => {
    try {
      const [s, reg] = await Promise.all([
        twilioA2pApi.getAppointmentSettings(),
        twilioA2pApi.status().catch(() => null),
      ]);
      setSettings(s);
      // Single canonical A2P campaign (Home Service Appointment Notifications)
      // covers confirmation/reminder/reschedule — same status for all 3 rows.
      setCampaignStatus(reg?.campaignStatus);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const data = await twilioA2pApi.saveAppointmentSettings(settings);
      setSettings(data);
      toast.success("Appointment messaging settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const setConfirmationEnabled = (v) => setSettings((s) => ({ ...s, confirmation: { enabled: v } }));
  const setReminderEnabled = (v) => setSettings((s) => ({ ...s, reminder: { ...s.reminder, enabled: v } }));
  const setReminderField = (i, field, val) =>
    setSettings((s) => {
      const reminders = s.reminder.reminders.map((r, idx) => (idx === i ? { ...r, [field]: val } : r));
      return { ...s, reminder: { ...s.reminder, reminders } };
    });
  const addReminder = () =>
    setSettings((s) => ({
      ...s,
      reminder: { ...s.reminder, reminders: [...s.reminder.reminders, { value: 2, unit: "hours" }].slice(0, 2) },
    }));
  const removeReminder = (i) =>
    setSettings((s) => ({
      ...s,
      reminder: { ...s.reminder, reminders: s.reminder.reminders.filter((_, idx) => idx !== i) },
    }));
  const setRescheduleEnabled = (v) => setSettings((s) => ({ ...s, reschedule: { ...s.reschedule, enabled: v } }));
  const setRescheduleField = (field, val) => setSettings((s) => ({ ...s, reschedule: { ...s.reschedule, [field]: val } }));

  if (loading || !settings) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-center h-24" data-testid="appointment-messaging-loading">
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="appointment-messaging-card">
      <div className="flex items-center gap-2 mb-1">
        <CalendarClock className="h-4.5 w-4.5 text-blue-600" />
        <h3 className="text-[14.5px] font-bold text-slate-900">Appointment Messaging</h3>
      </div>
      <p className="text-[12.5px] text-slate-500 mb-4">
        Control when automated appointment SMS messages are sent to your customers, within your Twilio A2P compliant campaigns.
      </p>

      <div className="space-y-5">
        {/* Confirmation */}
        <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-100" data-testid="appointment-confirmation-row">
          <div>
            <div className="text-[13px] font-semibold text-slate-800">Appointment Confirmation</div>
            <div className="text-[12px] text-slate-500">Sends immediately when an appointment is booked or confirmed.</div>
            <CampaignWarning status={campaignStatus} testId="a2p-appointment-campaign-warning-confirmation" />
          </div>
          <Toggle checked={settings.confirmation.enabled} onChange={setConfirmationEnabled} testId="appointment-confirmation-toggle" />
        </div>

        {/* Reminder */}
        <div className="pb-4 border-b border-slate-100" data-testid="appointment-reminder-row">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold text-slate-800">Appointment Reminder</div>
              <div className="text-[12px] text-slate-500">Send up to 2 reminders before the scheduled appointment.</div>
              <CampaignWarning status={campaignStatus} testId="a2p-appointment-campaign-warning-reminder" />
            </div>
            <Toggle checked={settings.reminder.enabled} onChange={setReminderEnabled} testId="appointment-reminder-toggle" />
          </div>
          {settings.reminder.enabled && (
            <div className="mt-3 space-y-2">
              {settings.reminder.reminders.map((r, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`appointment-reminder-entry-${i}`}>
                  <span className="text-[12.5px] text-slate-600">Send reminder</span>
                  <input
                    type="number"
                    min={1}
                    value={r.value}
                    onChange={(e) => setReminderField(i, "value", Number(e.target.value))}
                    data-testid={`appointment-reminder-value-${i}`}
                    className={timingInputClass}
                  />
                  <select
                    value={r.unit}
                    onChange={(e) => setReminderField(i, "unit", e.target.value)}
                    data-testid={`appointment-reminder-unit-${i}`}
                    className={timingSelectClass}
                  >
                    {UNIT_OPTIONS.map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                  <span className="text-[12.5px] text-slate-600">before the appointment</span>
                  {settings.reminder.reminders.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeReminder(i)}
                      data-testid={`appointment-reminder-remove-${i}`}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              ))}
              {settings.reminder.reminders.length < 2 && (
                <button
                  type="button"
                  onClick={addReminder}
                  data-testid="appointment-reminder-add-btn"
                  className="flex items-center gap-1 text-[12.5px] font-semibold text-blue-600 hover:text-blue-700"
                >
                  <Plus className="h-3.5 w-3.5" /> Add another reminder
                </button>
              )}
            </div>
          )}
        </div>

        {/* Reschedule */}
        <div data-testid="appointment-reschedule-row">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[13px] font-semibold text-slate-800">Appointment Reschedule</div>
              <div className="text-[12px] text-slate-500">Offer to reschedule after a missed appointment.</div>
              <CampaignWarning status={campaignStatus} testId="a2p-appointment-campaign-warning-reschedule" />
            </div>
            <Toggle checked={settings.reschedule.enabled} onChange={setRescheduleEnabled} testId="appointment-reschedule-toggle" />
          </div>
          {settings.reschedule.enabled && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-[12.5px] text-slate-600">Send reschedule message</span>
              <input
                type="number"
                min={1}
                value={settings.reschedule.value}
                onChange={(e) => setRescheduleField("value", Number(e.target.value))}
                data-testid="appointment-reschedule-value"
                className={timingInputClass}
              />
              <select
                value={settings.reschedule.unit}
                onChange={(e) => setRescheduleField("unit", e.target.value)}
                data-testid="appointment-reschedule-unit"
                className={timingSelectClass}
              >
                {UNIT_OPTIONS.map((u) => (
                  <option key={u.value} value={u.value}>{u.label}</option>
                ))}
              </select>
              <span className="text-[12.5px] text-slate-600">after the missed appointment</span>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-5 mt-1">
        <Button onClick={save} disabled={saving} data-testid="appointment-messaging-save-btn" className="h-10 bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
